import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { JOB_TYPES } from "@/lib/jobTypes";
import { ASE_OPTIONS } from "@/components/ase/ASEFormConstants";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Zap,
  Folder,
  FolderOpen,
  MapPin,
  Calendar,
  Trash2,
  DatabaseBackup,
  DollarSign,
  Users,
  Pencil,
  ArrowRightLeft,
  LogOut,
  Loader2,
  Radio,
  Shield,
  User,
  ChevronLeft,
  Search,
  X,
  Map as MapIcon,
  WifiOff,
  Copy,
  FolderInput,
} from "lucide-react";
import { LifeBuoy, Home, ClipboardList, Settings } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { getOfflineSavedJobs } from "@/lib/offlineTiles";
import { useAuth } from "@/context/AuthContext";
import { OfflineIndicator } from "@/components/OfflineIndicator";
import { JobsLocationMap } from "@/components/JobsLocationMap";
import { SortableJobTiles } from "@/components/SortableJobTiles";


const JobsList = ({ mode = "folders" }) => {
  const isHome = mode === "home";
  const navigate = useNavigate();
  const { user, logout, isAdmin, isSupervisor } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [offlineSavedJobs, setOfflineSavedJobs] = useState(() => getOfflineSavedJobs());
  useEffect(() => {
    const refresh = () => setOfflineSavedJobs(getOfflineSavedJobs());
    window.addEventListener("offline-saved-jobs-changed", refresh);
    return () => window.removeEventListener("offline-saved-jobs-changed", refresh);
  }, []);
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchParams] = useSearchParams();
  useEffect(() => { if (searchParams.get("new") === "1") setShowCreateDialog(true); }, [searchParams]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "", location: "", job_type: "OCALC", site_id: "", employee_info: "" });
  const [saving, setSaving] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  // Home tiles: per-user manual order (job ids), persisted on the server
  const [tileOrder, setTileOrder] = useState([]);
  useEffect(() => {
    if (!isHome) return;
    axios.get("/home/tile-order").then((r) => setTileOrder(r.data?.order || [])).catch(() => {});
  }, [isHome]);
  const saveTileOrder = async (order) => {
    setTileOrder(order);
    try { await axios.put("/home/tile-order", { order }); } catch { toast.error("Could not save tile order"); }
  };

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return jobs.filter((j) =>
      [j.name, j.location, j.description, j.job_type]
        .some((f) => (f || "").toLowerCase().includes(q))
    );
  }, [jobs, searchQuery]);

  // Group jobs into month folders by folder_month override or created_at (newest month first)
  const monthGroups = useMemo(() => {
    const groups = {};
    jobs.forEach((j) => {
      let key = /^\d{4}-\d{2}$/.test(j.folder_month || "") ? j.folder_month : null;
      if (!key) {
        const d = new Date(j.created_at);
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      }
      if (!groups[key]) {
        const [y, m] = key.split("-").map(Number);
        groups[key] = { key, label: new Date(y, m - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" }), jobs: [] };
      }
      groups[key].jobs.push(j);
    });
    if (isHome) {
      // Incomplete / in-progress jobs pinned above completed ones; newest created first within each group.
      // A manual drag order (if any) applies within a group, with brand-new (unranked) jobs still at the top.
      const rank = new Map(tileOrder.map((id, i) => [id, i]));
      const created = (j) => new Date(j.created_at).getTime() || 0;
      Object.values(groups).forEach((g) => {
        g.jobs.sort((a, b) => {
          if (!!a.completed !== !!b.completed) return a.completed ? 1 : -1;
          const ra = rank.has(a.id) ? rank.get(a.id) : -1;
          const rb = rank.has(b.id) ? rank.get(b.id) : -1;
          if (ra !== rb) return ra - rb;
          return created(b) - created(a);
        });
      });
    }
    return Object.values(groups).sort((a, b) => b.key.localeCompare(a.key));
  }, [jobs, isHome, tileOrder]);

  // Reorder one month's tiles and persist the full Home order (all months, display order)
  const reorderMonthTiles = (monthKey, orderedIds) => {
    const order = monthGroups.flatMap((g) => (g.key === monthKey ? orderedIds : g.jobs.map((j) => j.id)));
    saveTileOrder(order);
  };

  const activeGroup = monthGroups.find((g) => g.key === selectedMonth) || null;

  const fetchJobs = useCallback(async () => {
    try {
      const response = await axios.get("/jobs");
      setJobs(response.data);
    } catch (error) {
      toast.error("Failed to fetch jobs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleCreate = async () => {
    if (!(formData.site_id || "").trim()) {
      toast.error("Site ID is required");
      return;
    }
    setSaving(true);
    try {
      await axios.post("/jobs", formData);
      toast.success("Job created successfully!");
      setShowCreateDialog(false);
      setFormData({ name: "", description: "", location: "", job_type: "OCALC", site_id: "", employee_info: "" });
      fetchJobs();
    } catch (error) {
      toast.error("Failed to create job");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!(formData.site_id || "").trim()) {
      toast.error("Site ID is required");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`/jobs/${editingJob.id}`, formData);
      toast.success("Job updated successfully!");
      setShowEditDialog(false);
      setEditingJob(null);
      setFormData({ name: "", description: "", location: "", job_type: "OCALC", site_id: "", employee_info: "" });
      fetchJobs();
    } catch (error) {
      toast.error("Failed to update job");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/jobs/${deleteId}`);
      toast.success("Job moved to Deleted Items — restore it anytime from the trash folder");
      setDeleteId(null);
      fetchJobs();
    } catch (error) {
      toast.error("Failed to delete job");
    }
  };

  const openEditDialog = (job, e) => {
    e.stopPropagation();
    setEditingJob(job);
    setFormData({ name: job.name, description: job.description || "", location: job.location || "", job_type: job.job_type || "OCALC", site_id: job.site_id || "", employee_info: job.employee_info || "" });
    setShowEditDialog(true);
  };

  const handleToggleComplete = async (job, completed) => {
    setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, completed } : j)));
    try {
      await axios.put(`/jobs/${job.id}`, { completed });
      toast.success(completed ? "Job marked complete" : "Job marked incomplete");
    } catch (error) {
      setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, completed: !completed } : j)));
      toast.error("Failed to update job status");
    }
  };

  // Job transfer
  const [transferJob, setTransferJob] = useState(null);
  const [transferUsers, setTransferUsers] = useState([]);
  const [transferUserId, setTransferUserId] = useState("");
  const [transferring, setTransferring] = useState(false);

  const [moveMonthJob, setMoveMonthJob] = useState(null);
  const [moveMonthValue, setMoveMonthValue] = useState("");
  const [movingMonth, setMovingMonth] = useState(false);

  const duplicateJob = async (job, e) => {
    e.stopPropagation();
    try {
      await axios.post(`/jobs/${job.id}/duplicate`);
      toast.success("Job duplicated");
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to duplicate job");
    }
  };

  const openMoveMonthDialog = (job, e) => {
    e.stopPropagation();
    setMoveMonthJob(job);
    const d = new Date(job.created_at);
    setMoveMonthValue(/^\d{4}-\d{2}$/.test(job.folder_month || "") ? job.folder_month : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  const handleMoveMonth = async () => {
    if (!moveMonthJob || !/^\d{4}-\d{2}$/.test(moveMonthValue)) return;
    setMovingMonth(true);
    try {
      await axios.put(`/jobs/${moveMonthJob.id}`, { folder_month: moveMonthValue });
      toast.success("Job moved to new month");
      setMoveMonthJob(null);
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to move job");
    } finally {
      setMovingMonth(false);
    }
  };

  const openTransferDialog = async (job, e) => {
    e.stopPropagation();
    setTransferJob(job);
    setTransferUserId("");
    try {
      const res = await axios.get("/users/transferable");
      setTransferUsers(res.data || []);
    } catch (error) {
      toast.error("Failed to load users");
    }
  };

  const handleTransfer = async () => {
    if (!transferJob || !transferUserId) return;
    setTransferring(true);
    try {
      const res = await axios.post(`/jobs/${transferJob.id}/transfer`, { user_id: transferUserId });
      toast.success(res.data?.message || "Job transferred");
      setTransferJob(null);
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to transfer job");
    } finally {
      setTransferring(false);
    }
  };

  const handleToggleStarted = async (job, started) => {
    setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, started } : j)));
    try {
      await axios.put(`/jobs/${job.id}`, { started });
      toast.success(started ? "Job marked started / partial" : "Job no longer marked started");
    } catch {
      setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, started: !started } : j)));
      toast.error("Could not update job");
    }
  };

  const tileState = (job) => (job.completed ? "complete" : job.started ? "started" : "open");
  const renderJobCard = (job) => (
    <div
      key={job.id}
      onClick={() => navigate(`/jobs/${job.id}`)}
      className={`industrial-card p-5 cursor-pointer transition-colors group ${job.completed ? "bg-green-50 border-green-500/70 hover:border-green-600" : job.started ? "bg-yellow-50 border-yellow-500/70 hover:border-yellow-600" : "hover:border-red-500/50"}`}
      data-testid={`job-card-${job.id}`}
      data-state={tileState(job)}
    >
      {/* Started (partial) + Complete checkboxes */}
      <div className="flex items-center justify-between mb-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 select-none">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={!!job.started}
              onCheckedChange={(v) => handleToggleStarted(job, !!v)}
              className="data-[state=checked]:bg-yellow-500 data-[state=checked]:border-yellow-500"
              data-testid={`job-started-checkbox-${job.id}`}
            />
            <span className={`text-xs font-mono uppercase mr-3 ${job.started ? "text-yellow-700 font-bold" : "text-zinc-500"}`}>
              {"Started"}
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox
            checked={!!job.completed}
            onCheckedChange={(v) => handleToggleComplete(job, !!v)}
            className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
            data-testid={`job-complete-checkbox-${job.id}`}
          />
          <span className={`text-xs font-mono uppercase ${job.completed ? "text-green-600 font-bold" : "text-zinc-500"}`}>
            {job.completed ? "Complete" : "Mark Complete"}
          </span>
          </label>
          {offlineSavedJobs[job.id] && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wide bg-sky-100 text-sky-700 border border-sky-300 rounded px-1.5 py-0.5 ml-1"
              title={`Saved for offline use ${new Date(offlineSavedJobs[job.id]).toLocaleDateString()}`}
              data-testid={`job-offline-badge-${job.id}`}
            >
              <WifiOff className="w-3 h-3" />
              Saved Offline
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-zinc-600 hover:text-emerald-600"
            title="Duplicate job"
            onClick={(e) => duplicateJob(job, e)}
            data-testid={`job-duplicate-btn-${job.id}`}
          >
            <Copy className="w-4 h-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-zinc-600 hover:text-violet-600"
            title="Move job to another month"
            onClick={(e) => openMoveMonthDialog(job, e)}
            data-testid={`job-move-month-btn-${job.id}`}
          >
            <FolderInput className="w-4 h-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-zinc-600 hover:text-blue-600"
            title="Transfer job to another user"
            onClick={(e) => openTransferDialog(job, e)}
            data-testid={`job-transfer-btn-${job.id}`}
          >
            <ArrowRightLeft className="w-4 h-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-zinc-600 hover:text-zinc-900"
            onClick={(e) => openEditDialog(job, e)}
            data-testid={`job-edit-btn-${job.id}`}
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-zinc-600 hover:text-red-600"
            onClick={(e) => { e.stopPropagation(); setDeleteId(job.id); }}
            data-testid={`job-delete-btn-${job.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 flex items-center justify-center ${job.completed ? "bg-green-600/20 border border-green-500/30" : job.started ? "bg-yellow-400/30 border border-yellow-500/50" : "bg-red-600/20 border border-red-500/30"}`}>
            <Folder className={`w-5 h-5 ${job.completed ? "text-green-600" : job.started ? "text-yellow-600" : "text-red-600"}`} />
          </div>
          <div>
            <h3 className={`font-heading text-lg font-bold text-zinc-900 transition-colors ${job.completed ? "group-hover:text-green-600" : "group-hover:text-red-600"}`}>
              {job.site_id || job.name}
            </h3>
            {job.name && job.site_id && (
              <div className="text-xs text-zinc-500 font-mono">{job.name}</div>
            )}
            {job.location && (
              <div className="flex items-center gap-1 text-xs text-zinc-500">
                <MapPin className="w-3 h-3" />
                {job.location}
              </div>
            )}
            {isAdmin && job.user_email && (
              <div className="flex items-center gap-1 text-xs text-blue-600 mt-1">
                <User className="w-3 h-3" />
                {job.user_email}
              </div>
            )}
            {job.transferred_from && (
              <div className="flex items-center gap-1 text-xs text-amber-600 mt-1" data-testid={`job-transferred-from-${job.id}`}>
                <ArrowRightLeft className="w-3 h-3" />
                Transferred from {job.transferred_from}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {job.description && (
        <p className="text-sm text-zinc-600 mb-3 line-clamp-2">{job.description}</p>
      )}
      
      <div className="flex items-center justify-between pt-3 border-t border-zinc-200">
        <div className="flex items-center gap-2 text-sm">
          <Radio className="w-4 h-4 text-yellow-500" />
          <span className="text-zinc-700 font-mono">{job.pole_count}</span>
          <span className="text-zinc-500">poles</span>
        </div>
        <div className="flex items-center gap-1 text-xs text-zinc-500">
          <Calendar className="w-3 h-3" />
          {new Date(job.created_at).toLocaleDateString()}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-industrial-bg flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-red-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-industrial-bg" data-testid={isHome ? "home-page" : "jobs-list"}>
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-red-600 flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-lg sm:text-2xl font-black tracking-tight uppercase text-zinc-900">
                  Pole Data Sheet
                </h1>
                <p className="text-xs text-zinc-500 font-mono tracking-wider hidden sm:block">
                  {isAdmin ? "ADMIN VIEW - ALL JOBS" : "SELECT OR CREATE A JOB"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <OfflineIndicator />
              <Button onClick={() => navigate(isHome ? "/jobs" : "/")} variant="outline" size="sm" className="bg-zinc-100 border-zinc-300 hover:bg-zinc-200 text-zinc-900 gap-1.5" title={isHome ? "Jobs list by month folders" : "Home — job map & monthly tiles"} data-testid={isHome ? "jobs-folders-btn" : "home-nav-btn"}>
                {isHome ? <Folder className="w-4 h-4" /> : <Home className="w-4 h-4" />}
                <span className="hidden md:inline">{isHome ? "Month Folders" : "Home"}</span>
              </Button>
              {isHome && (
                <Button onClick={() => navigate("/field-report")} variant="outline" size="sm" className="bg-zinc-100 border-zinc-300 hover:bg-zinc-200 text-zinc-900 gap-1.5" title="Daily Field Report" data-testid="daily-reports-btn">
                  <ClipboardList className="w-4 h-4" />
                  <span className="hidden md:inline">Daily Reports</span>
                </Button>
              )}
              {(isAdmin || isSupervisor) && (
                <Button onClick={() => navigate("/admin/team-reports")} variant="outline" size="sm" className="bg-zinc-100 border-zinc-300 hover:bg-zinc-200 text-zinc-900 gap-1.5" title="Team Daily Field Reports" data-testid="team-reports-btn">
                  <ClipboardList className="w-4 h-4" />
                  <span className="hidden md:inline">Team Reports</span>
                </Button>
              )}
              {/* Settings overflow: tools that are rarely needed day-to-day */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-zinc-500 hover:text-zinc-900" title="Settings & tools" data-testid="header-settings-btn">
                    <Settings className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60 bg-white z-[1100]" data-testid="header-settings-menu">
                  <DropdownMenuLabel className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase">Tools</DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={async () => {
                      if (!window.confirm("Fill in the lot address (building # + street) for every GPS'd pole and attribute that has none yet?")) return;
                      setBackfilling(true);
                      try {
                        const r = await axios.post("/maps/backfill-addresses");
                        toast.success(`Addresses filled: ${r.data.filled} (${r.data.no_match} without a nearby home) across ${r.data.jobs} job${r.data.jobs === 1 ? "" : "s"}`);
                      } catch (e) {
                        toast.error("Address backfill failed");
                      } finally { setBackfilling(false); }
                    }}
                    disabled={backfilling}
                    className="cursor-pointer"
                    data-testid="backfill-addresses-btn"
                  >
                    {backfilling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Home className="w-4 h-4 mr-2" />}
                    Fill missing item addresses
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/recovery")} className="cursor-pointer" data-testid="device-recovery-btn">
                    <LifeBuoy className="w-4 h-4 mr-2" />
                    Device data recovery
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/trash")} className="cursor-pointer" data-testid="deleted-items-btn">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Deleted items (Trash)
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase">Admin</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => navigate("/admin/users")} className="cursor-pointer" data-testid="admin-users-btn">
                        <Users className="w-4 h-4 mr-2" />
                        Users by company
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/cost-rates")} className="cursor-pointer" data-testid="admin-cost-rates-btn">
                        <DollarSign className="w-4 h-4 mr-2" />
                        Cost report rates
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/backup")} className="cursor-pointer" data-testid="backup-atlas-btn">
                        <DatabaseBackup className="w-4 h-4 mr-2" />
                        Backup to Atlas
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              {user && (
                <div className="hidden sm:flex items-center gap-2">
                  {isAdmin && (
                    <span className="flex items-center gap-1 px-2 py-1 bg-amber-500/20 border border-amber-500/50 text-amber-400 text-xs font-mono rounded">
                      <Shield className="w-3 h-3" />
                      ADMIN
                    </span>
                  )}
                  <span className="text-xs text-zinc-600 font-mono">
                    {user.name || user.email}
                  </span>
                </div>
              )}
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="flex-1 sm:flex-initial h-11 sm:h-10 bg-red-600 hover:bg-red-700 text-white"
                data-testid="create-job-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Job
              </Button>
              <Button
                onClick={logout}
                variant="ghost"
                size="icon"
                className="h-11 w-11 sm:h-10 sm:w-10 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6">
        {jobs.length === 0 ? (
          <div className="industrial-card p-12 text-center">
            <Folder className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-zinc-900 mb-2">No Jobs Yet</h2>
            <p className="text-zinc-600 mb-6">Create your first job to start collecting pole data</p>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create First Job
            </Button>
          </div>
        ) : (
          <>
            {/* Map of all job locations */}
            <JobsLocationMap jobs={jobs} onOpenJob={(id) => navigate(`/jobs/${id}`)} />

            {/* Search bar */}
            <div className="relative mb-6 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search jobs by name, location, or type..."
                className="pl-9 pr-9 bg-white border-zinc-300 text-zinc-900 h-11 sm:h-10"
                data-testid="job-search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                  data-testid="job-search-clear-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {searchQuery.trim() ? (
              /* Search results view */
              <>
                <div className="flex items-center gap-2 mb-4">
                  <Search className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-zinc-600 font-mono" data-testid="job-search-results-count">
                    {searchResults.length} job{searchResults.length === 1 ? "" : "s"} found for "{searchQuery.trim()}"
                  </span>
                </div>
                {searchResults.length === 0 ? (
                  <div className="industrial-card p-8 text-center text-zinc-500" data-testid="job-search-no-results">
                    No jobs match your search.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="job-search-results-grid">
                    {searchResults.map((job) => renderJobCard(job))}
                  </div>
                )}
              </>
            ) : isHome ? (
              /* Home: every month expanded, full job tiles with status boxes + actions */
              <div className="space-y-6" data-testid="home-months">
                {monthGroups.map((group) => (
                  <section key={group.key} data-testid={`home-month-${group.key}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <FolderOpen className="w-5 h-5 text-red-600" />
                      <h2 className="font-heading text-base md:text-lg font-bold text-zinc-900 uppercase">{group.label}</h2>
                      <span className="text-xs text-zinc-500 font-mono">({group.jobs.length} job{group.jobs.length === 1 ? "" : "s"})</span>
                    </div>
                    <SortableJobTiles
                      jobs={group.jobs}
                      renderJobCard={renderJobCard}
                      onReorder={(ids) => reorderMonthTiles(group.key, ids)}
                      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                    />
                  </section>
                ))}
              </div>
            ) : activeGroup === null ? (
              /* Month folders view */
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="month-folders-grid">
                {monthGroups.map((group) => (
                  <div
                    key={group.key}
                    onClick={() => setSelectedMonth(group.key)}
                    className="industrial-card p-5 cursor-pointer hover:border-red-500/50 transition-colors group"
                    data-testid={`month-folder-${group.key}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-red-600/20 border border-red-500/30 flex items-center justify-center flex-shrink-0">
                        <Folder className="w-6 h-6 text-red-600" />
                      </div>
                      <div>
                        <h3 className="font-heading text-lg font-bold text-zinc-900 group-hover:text-red-600 transition-colors">
                          {group.label}
                        </h3>
                        <p className="text-xs text-zinc-500 font-mono">
                          {group.jobs.length} job{group.jobs.length === 1 ? "" : "s"}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Jobs inside selected month */
              <>
                <div className="flex items-center gap-3 mb-4">
                  <Button
                    variant="outline"
                    onClick={() => setSelectedMonth(null)}
                    className="h-9 bg-zinc-100 border-zinc-300 hover:bg-zinc-200 text-zinc-900"
                    data-testid="back-to-months-btn"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    All Months
                  </Button>
                  <div className="flex items-center gap-2">
                    <FolderOpen className="w-5 h-5 text-red-600" />
                    <h2 className="font-heading text-lg font-bold text-zinc-900 uppercase">{activeGroup.label}</h2>
                    <span className="text-xs text-zinc-500 font-mono">({activeGroup.jobs.length} job{activeGroup.jobs.length === 1 ? "" : "s"})</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activeGroup.jobs.map((job) => renderJobCard(job))}
                </div>
              </>
            )}
          </>
        )}
      </main>

      {/* Create Job Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-zinc-50 border-zinc-200">
          <DialogHeader>
            <DialogTitle className="text-zinc-900">Create New Job</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">Set the site ID, wire center, type and location details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-xs font-mono text-zinc-500 uppercase">Site ID *</Label>
              <Input
                value={formData.site_id || ""}
                onChange={(e) => setFormData({ ...formData, site_id: e.target.value })}
                placeholder="e.g., A05CZ38"
                className="bg-white border-zinc-300 text-zinc-900"
                data-testid="job-site-id-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono text-zinc-500 uppercase">Wire Center</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Main Street Project"
                className="bg-white border-zinc-300 text-zinc-900"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono text-zinc-500 uppercase">Employee Info</Label>
              <Select value={formData.employee_info || ""} onValueChange={(v) => setFormData({ ...formData, employee_info: v === "__none__" ? "" : v })}>
                <SelectTrigger className="bg-white border-zinc-300 text-zinc-900" data-testid="job-employee-select">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-50 border-zinc-300 max-h-64">
                  <SelectItem value="__none__" className="text-zinc-500">—</SelectItem>
                  {ASE_OPTIONS.employees.map((emp) => (<SelectItem key={emp} value={emp}>{emp}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono text-zinc-500 uppercase">Job Type</Label>
              <Select value={formData.job_type} onValueChange={(v) => setFormData({ ...formData, job_type: v })}>
                <SelectTrigger className="bg-white border-zinc-300 text-zinc-900" data-testid="job-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-50 border-zinc-300">
                  {JOB_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono text-zinc-500 uppercase">Business Name</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., Blue Dog Landwerks"
                className="bg-white border-zinc-300 text-zinc-900"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono text-zinc-500 uppercase">Location</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g., Dallas, TX"
                className="bg-white border-zinc-300 text-zinc-900"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="bg-zinc-100 border-zinc-300 text-zinc-900">
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Job"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Job Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-zinc-50 border-zinc-200">
          <DialogHeader>
            <DialogTitle className="text-zinc-900">Edit Job</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">Update the site ID, wire center, type and location details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-xs font-mono text-zinc-500 uppercase">Site ID *</Label>
              <Input
                value={formData.site_id || ""}
                onChange={(e) => setFormData({ ...formData, site_id: e.target.value })}
                className="bg-white border-zinc-300 text-zinc-900"
                data-testid="job-edit-site-id-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono text-zinc-500 uppercase">Wire Center</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-white border-zinc-300 text-zinc-900"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono text-zinc-500 uppercase">Employee Info</Label>
              <Select value={formData.employee_info || ""} onValueChange={(v) => setFormData({ ...formData, employee_info: v === "__none__" ? "" : v })}>
                <SelectTrigger className="bg-white border-zinc-300 text-zinc-900" data-testid="job-edit-employee-select">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-50 border-zinc-300 max-h-64">
                  <SelectItem value="__none__" className="text-zinc-500">—</SelectItem>
                  {ASE_OPTIONS.employees.map((emp) => (<SelectItem key={emp} value={emp}>{emp}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono text-zinc-500 uppercase">Job Type</Label>
              <Select value={formData.job_type} onValueChange={(v) => setFormData({ ...formData, job_type: v })}>
                <SelectTrigger className="bg-white border-zinc-300 text-zinc-900">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-50 border-zinc-300">
                  {JOB_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono text-zinc-500 uppercase">Business Name</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-white border-zinc-300 text-zinc-900"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono text-zinc-500 uppercase">Location</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="bg-white border-zinc-300 text-zinc-900"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="bg-zinc-100 border-zinc-300 text-zinc-900">
              Cancel
            </Button>
            <Button onClick={handleEdit} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move to Month Dialog */}
      <Dialog open={!!moveMonthJob} onOpenChange={(o) => { if (!o) setMoveMonthJob(null); }}>
        <DialogContent className="bg-white border-zinc-200 max-w-sm" aria-describedby={undefined} data-testid="move-month-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-900 flex items-center gap-2">
              <FolderInput className="w-5 h-5 text-violet-600" />
              Move Job to Month
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-zinc-700">Pick the month folder for "{moveMonthJob?.site_id || moveMonthJob?.name}"</Label>
            <input
              type="month"
              value={moveMonthValue}
              onChange={(e) => setMoveMonthValue(e.target.value)}
              className="w-full h-11 px-3 bg-white border border-zinc-300 rounded-md text-zinc-900"
              data-testid="move-month-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveMonthJob(null)} className="bg-zinc-100 border-zinc-300 text-zinc-900" data-testid="move-month-cancel">Cancel</Button>
            <Button onClick={handleMoveMonth} disabled={movingMonth || !/^\d{4}-\d{2}$/.test(moveMonthValue)} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="move-month-confirm">
              {movingMonth ? <Loader2 className="w-4 h-4 animate-spin" /> : "Move Job"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Job Dialog */}
      <Dialog open={!!transferJob} onOpenChange={(open) => { if (!open) setTransferJob(null); }}>
        <DialogContent className="bg-zinc-50 border-zinc-200" data-testid="transfer-job-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-900 flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-blue-600" />
              Transfer Job
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-600">
              Transfer <span className="font-bold text-zinc-900">{transferJob?.name}</span> to another user. It will appear on their dashboard{!isAdmin ? " and leave yours" : ""}.
            </p>
            <div className="space-y-2">
              <Label className="text-xs font-mono tracking-widest text-zinc-500 uppercase">Transfer To</Label>
              <Select value={transferUserId} onValueChange={setTransferUserId}>
                <SelectTrigger className="bg-white border-zinc-300 text-zinc-900" data-testid="transfer-user-select">
                  <SelectValue placeholder={transferUsers.length ? "Select a user" : "Loading users..."} />
                </SelectTrigger>
                <SelectContent className="bg-zinc-50 border-zinc-300 max-h-64">
                  {transferUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id} data-testid={`transfer-user-${u.email}`}>{u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {transferUsers.length === 0 && (
                <p className="text-xs text-zinc-500">No other company users found.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferJob(null)} className="bg-zinc-100 border-zinc-300 text-zinc-900">
              Cancel
            </Button>
            <Button onClick={handleTransfer} disabled={!transferUserId || transferring} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="transfer-confirm-btn">
              {transferring ? <Loader2 className="w-4 h-4 animate-spin" /> : "Transfer Job"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-zinc-50 border-zinc-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-900">Delete Job?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600">
              This will delete this job and EVERYTHING inside it (poles, attributes, photos and map data). It will be moved to your Deleted Items folder, where you can restore it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-100 border-zinc-300 text-zinc-900" data-testid="job-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="job-delete-confirm-btn">
              Delete Job
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default JobsList;
